import { Directive, DirectiveBinding } from 'vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';

export interface FeatureFlagDirectiveValue {
  flag: string;
  defaultValue?: boolean;
  inverse?: boolean; // Show element when flag is disabled
}

/**
 * Vue directive for conditionally showing/hiding elements based on feature flags
 * 
 * Usage examples:
 * v-feature-flag="'integrations'"
 * v-feature-flag="{ flag: 'integrations', defaultValue: false }"
 * v-feature-flag="{ flag: 'integrations', inverse: true }" // Show when disabled
 */
export const vFeatureFlag: Directive = {
  mounted(el: HTMLElement, binding: DirectiveBinding<string | FeatureFlagDirectiveValue>) {
    updateElementVisibility(el, binding);
  },
  updated(el: HTMLElement, binding: DirectiveBinding<string | FeatureFlagDirectiveValue>) {
    updateElementVisibility(el, binding);
  }
};

function updateElementVisibility(el: HTMLElement, binding: DirectiveBinding<string | FeatureFlagDirectiveValue>) {
  const { isFeatureEnabled } = useFeatureFlags();
  
  let flagName: string;
  let defaultValue = false;
  let inverse = false;
  
  // Handle different binding value formats
  if (typeof binding.value === 'string') {
    flagName = binding.value;
  } else if (binding.value && typeof binding.value === 'object') {
    flagName = binding.value.flag;
    defaultValue = binding.value.defaultValue ?? false;
    inverse = binding.value.inverse ?? false;
  } else {
    console.warn('v-feature-flag directive requires a flag name or configuration object');
    return;
  }
  
  const isEnabled = isFeatureEnabled(flagName, defaultValue);
  const shouldShow = inverse ? !isEnabled : isEnabled;
  
  // Use CSS display instead of removing from DOM to maintain component lifecycle
  el.style.display = shouldShow ? '' : 'none';
  
  // Add data attribute for debugging
  el.setAttribute('data-feature-flag', flagName);
  el.setAttribute('data-feature-enabled', isEnabled.toString());
}
